import React, { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Connect,
  PropsFromRedux,
} from './SearchQuerySectionPreparatoryWorkContainer';

import { SearchBarSection } from '../../search-bar-section/SearchBarSection';
import { RawSearchResultPreparatoryWork } from '../../../../../../../search/components/search-results/preparatory-work/SearchResultPreparatoryWork';
import { Loader } from '../../../../../../../components/loader/Loader';
import { SeeMoreMessage } from '../SeeMoreMessage';
import { ResetQueryMessage } from '../ResetQueryMessage';

import { useRestrictedModal } from '../../../../../../../components/modal/useRestrictedModal';
import { useUser } from '../../../../../../../user/useUser';
import { searchWithinRestrictedArticles } from '../../../../../legislation.api';
import { doctrineAnalytics } from '../../../../../../../utils/analyticsUtils';
import { getLegislativeFolderAggregation } from '../utils/utils';
import { LegislationContextType } from '../../../../../legislation.types';

import styles from './search_query_section_preparatory_work.module.scss';

type Props = {
  preparatoryWork: LegislationContextType['preparatoryWork'];
  articleTitle: string;
  articleIds: string[];
  articleId: string;
  initialVersionId: string;
  textNature: string;
} & PropsFromRedux;

export const RawSearchQuerySectionPreparatoryWork: FC<Props> = ({
  preparatoryWork,
  articleTitle,
  articleIds,
  articleId,
  initialVersionId,
  textNature,
  query,
}) => {
  const intl = useIntl();
  const { openRestrictedModal } = useRestrictedModal();
  const { loggedIn } = useUser();
  const [currentQuery, setCurrentQuery] = useState<string>(query);
  const shouldPersistQuery = !!(loggedIn && query);
  const [resultHits, setResultHits] = useState(
    shouldPersistQuery ? [] : preparatoryWork.data?.hits,
  );
  const [resultTotalHits, setResultTotalHits] = useState(
    shouldPersistQuery ? 0 : preparatoryWork.data?.totalHits,
  );
  const [resultTheEnd, setResultTheEnd] = useState(
    shouldPersistQuery ? false : preparatoryWork.data?.theEnd,
  );
  const [loading, setLoading] = useState(false);
  const [aggregationsFilters, setAggregationsFilters] = useState<{
    legislativeFolder: { name: string; count: number }[];
  }>({
    legislativeFolder: preparatoryWork.filters?.legislativeFolder || [],
  });
  const [filterValue, setFilterValue] = useState({
    legislativeFolder: '',
  });

  const onChangeLegislativeFolder = async (value: string) => {
    if (!loggedIn) {
      openRestrictedModal({ source: 'preparatory_work_LegislativeFolder' });
      return;
    }
    setFilterValue({ legislativeFolder: value });
    setLoading(true);
    doctrineAnalytics('Legislation PreparatoryWorkCategoryClicked', {
      category_name: 'LegislativeFolder',
      article_version_id: articleId,
      article_id: initialVersionId,
      legislationType: textNature,
    });
    const result = await searchWithinRestrictedArticles({
      q: currentQuery,
      type: 'preparatory_work',
      restrict_to_law_article_ids: articleIds,
      size: 6,
      ...(value.length && { pwLegislativeFolder: value }),
    });
    setResultHits(result.hits);
    setResultTotalHits(result.totalHits);
    setResultTheEnd(result.theEnd);
    setLoading(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const result = await searchWithinRestrictedArticles({
        q: currentQuery,
        type: 'preparatory_work',
        restrict_to_law_article_ids: articleIds,
        size: 6,
      });
      setAggregationsFilters({
        legislativeFolder: getLegislativeFolderAggregation(result.aggregations),
      });
      setResultHits(result.hits);
      setResultTotalHits(result.totalHits);
      setResultTheEnd(result.theEnd);
      setLoading(false);
    };

    if (shouldPersistQuery && query.length) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClearClicked = async () => {
    setLoading(true);
    const result = await searchWithinRestrictedArticles(
      {
        type: 'preparatory_work',
        restrict_to_law_article_ids: articleIds,
        size: 6,
      },
      loggedIn,
    );
    setResultHits(result.hits);
    setResultTotalHits(result.totalHits);
    setResultTheEnd(result.theEnd);
    setCurrentQuery('');
    setLoading(false);
  };

  const renderFilterResults = () => {
    if (loading) return <Loader color="gray" size={48} strokeWidth={4} />;

    if (!resultHits?.length)
      return (
        <ResetQueryMessage
          type="preparatory_work"
          translationId="page/components/SearchQuerySection.tsx/6"
          articleTitle={articleTitle}
          query={currentQuery}
          onResetQueryMessageClicked={onClearClicked}
        />
      );
    if (resultHits) {
      return (
        <>
          {/* @ts-ignore */}
          <RawSearchResultPreparatoryWork hits={resultHits} />
          {!resultTheEnd && (
            <SeeMoreMessage
              type="preparatory_work"
              query={currentQuery}
              articleIds={articleIds}
              loggedIn={loggedIn}
              filterValue={filterValue}
              resultTotalHits={resultTotalHits || 0}
              modal="preparatory-work"
            />
          )}
          {resultTheEnd && currentQuery && (
            <ResetQueryMessage
              type="preparatory_work"
              translationId="page/components/SearchQuerySection.tsx/12"
              articleTitle={articleTitle}
              query={currentQuery}
              onResetQueryMessageClicked={onClearClicked}
            />
          )}
        </>
      );
    }
  };

  return (
    <>
      <SearchBarSection
        articleIds={articleIds}
        modal="preparatory-work"
        type="preparatory_work"
        filterValue={filterValue}
        queryFromState={currentQuery}
        shouldPersistQuery={shouldPersistQuery}
        onClearClicked={onClearClicked}
      />
      <div className={styles.filtersToggleContainer}>
        <select
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
            onChangeLegislativeFolder(event.target.value)
          }
          className={styles.selectLegislativeFolder}
        >
          <option value="">
            {intl.formatMessage({
              id:
                'components/preparatory-work/SearchQuerySectionPreparatoryWork.tsx/1',
            })}
          </option>
          {aggregationsFilters.legislativeFolder.map(({ name, count }) => (
            <option key={name} value={name}>
              {name} ({count})
            </option>
          ))}
        </select>
      </div>
      {renderFilterResults()}
    </>
  );
};

export const SearchQuerySectionPreparatoryWork = Connect(
  RawSearchQuerySectionPreparatoryWork,
);
